/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from "react";
import { IconX } from "@tabler/icons-react";
import FileUploader from "../../../components/common/FileUploader";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  useFetchFmOrderTemplate,
  useFmOrderBulkDocMutation,
} from "../../../apis/orderApis";
import toast from "react-hot-toast";
import {
  styleAndProtectDummyRow,
  cleanUploadedExcelFile,
} from "../../../utilities/bulkUploadHelper";

type FmBulkUploadModalsProps = {
  onClose: () => void;
  onResponseOpene: () => void;
  setOrderBulkResponse: React.Dispatch<React.SetStateAction<any>>;
};

export default function FmBulkUploadModals({
  onClose,
  onResponseOpene,
  setOrderBulkResponse,
}: FmBulkUploadModalsProps) {
  const [file, setFile] = useState<File | null>(null);

  const { mutate: uploadDocMutate, isPending: isBulkUploading } =
    useFmOrderBulkDocMutation();
  const { data: templateData, refetch: fetchTemplates } =
    useFetchFmOrderTemplate();

  const handleUploadDoc = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    const cleanedFile = await cleanUploadedExcelFile(file, templateData?.[0]);

    uploadDocMutate(
      { orders: cleanedFile },
      {
        onSuccess: (data: any) => {
          setOrderBulkResponse(data.data);
          onClose();
          onResponseOpene();
          toast.success("FM Orders uploaded successfully");
        },
        onError: (error: any) => {
          console.log(error, "error");
          toast.error(`${error}`);
        },
      },
    );
  };

  const handleDownloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("FM Orders Template");

    // Row 1: Info
    const infoRow = worksheet.addRow([
      "FM Orders Template — One product per row. For multiple products in the same order, repeat the same Reference No & Client ID on separate rows with different Product IDs. Do NOT modify the first 4 rows (Row 4 is sample/dummy data).",
    ]);
    worksheet.mergeCells(`A1:S1`);
    infoRow.eachCell((cell: any) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "E2EFD9" },
      };
      cell.font = {
        name: "Arial",
        size: 10,
        bold: true,
        color: { argb: "212121" },
      };
      cell.alignment = {
        horizontal: "left",
        vertical: "middle",
        wrapText: true,
      };
      cell.border = {
        top: { style: "thin", color: { argb: "FF000000" } },
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
    });
    infoRow.height = 30;

    // Row 2: Validation Rules
    const validationRow = worksheet.addRow([
      "Client ID is required",
      "Reference No is required",
      "Receiver Name is required",
      "Receiver Phone (9-12 digits) is required",
      "Receiver Email (optional)",
      "Receiver Country is required",
      "Receiver City (optional if National Address provided)",
      "Receiver District / Area (optional)",
      "Receiver Address (optional if National Address provided)",
      "National Address (optional if City+Address provided)",
      "Shipment Cost (optional)",
      "Payment Type: CC or COD",
      "COD Amount (0 for CC, required if COD)",
      "No of Pieces is required (numeric ≥1)",
      "Service (optional)",
      "Description (optional)",
      "Latitude (optional)",
      "Longitude (optional)",
      "Product ID is required (SKU) — one per row",
      "Quantity is required (numeric ≥1)",
    ]);
    validationRow.eachCell((cell: any) => {
      let fillColor = "FFCCCC"; // default light red for mandatory

      const optionalCols = [5, 7, 8, 9, 10, 11, 13, 15, 16, 17, 18];
      if (optionalCols.includes(Number(cell.col))) {
        fillColor = "FFEB9C"; // light yellow for optional
      }

      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: fillColor },
      };

      cell.alignment = {
        horizontal: "left",
        vertical: "middle",
        wrapText: true,
      };

      cell.font = { color: { argb: "212121" }, size: 9 };

      cell.border = {
        top: { style: "thin", color: { argb: "FF000000" } },
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
    });
    validationRow.height = 80;

    // Row 3: Column Headers — always use the definitive column list
    const headings = [
        "client_id",
        "reference_no",
        "receiver_name",
        "receiver_mobile",
        "receiver_email",
        "receiver_country",
        "receiver_city",
        "receiver_district",
        "receiver_address",
        "receiver_national_address",
        "shipment_cost",
        "payment_type",
        "cod_amount",
        "no_of_pieces",
        "service",
        "description",
        "latitude",
        "longitude",
        "product_id",
        "quantity",
      ];

    const headersRow = worksheet.addRow(headings);

    headersRow.eachCell((cell: any) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "217447" },
      };
      cell.font = { name: "Arial", bold: true, color: { argb: "ffffff" } };
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
      cell.border = {
        top: { style: "thin", color: { argb: "FF000000" } },
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
    });
    headersRow.height = 25;

    // Row 4: Dummy Data Row
    let dummyRow: ExcelJS.Row | undefined;
    if (templateData && templateData.length > 0 && templateData[0]) {
      const dummyValues = Object.values(templateData[0]);
      dummyRow = worksheet.addRow(dummyValues);
      dummyRow.eachCell((cell: any) => {
        cell.font = { name: "Arial", size: 10, color: { argb: "212121" } };
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        };
      });
      dummyRow.height = 20;
    }

    // Adjust column widths
    worksheet.columns = Array(headings.length).fill({ width: 22 });

    await styleAndProtectDummyRow(worksheet, dummyRow);

    // Export
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "fm-orders-template.xlsx");
    toast.success("FM Orders template downloaded successfully");
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-center">
      <div className="rounded-2xl">
        {/* Header */}
        <div className="flex items-center">
          <div className="bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] flex items-center justify-between px-6 py-4 w-full rounded-tl-3xl rounded-tr-[63px]">
            <h2 className="text-2xl">FM Bulk Upload Orders</h2>
          </div>
          <div className="relative">
            <button
              onClick={onClose}
              className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer w-12 h-12 ml-5 flex items-center justify-center flex-shrink-0"
            >
              <IconX size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="w-full py-4 px-6 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-tr-[30px]">
          <div className="w-[700px] space-y-4">
            <FileUploader
              name="csvFile"
              extraParentClasses="col-span-3"
              img="/img/upload-img.png"
              title="Upload CSV/Excel File"
              message="Upload a CSV or XLSX file with FM order data and product details (Max size: 2 MB)."
              classNameMain="bg-transparent border-none flex items-center gap-3"
              fileVal={file}
              setFileVal={(file: any) => {
                setFile(file);
              }}
              showPreview={true}
              required
            />

            {file && (
              <p className="text-sm text-green-600 font-medium">
                Selected File: {file.name}
              </p>
            )}

            <ul className="list-disc px-5 text-sm text-[#1E293B] font-medium space-y-1">
              <li>Download the Excel/CSV template before uploading.</li>
              <li>Do not change column headers.</li>
              <li>
                Include product information (Product ID & Quantity) for each
                order.
              </li>
              <li>
                Weight will be calculated from selected products - do not
                include weight column.
              </li>
              <li>
                Mandatory fields: Reference, Receiver Info, Shipment Details,
                and at least 1 Product.
              </li>
              <li>Receiver phone must be 9-10 characters.</li>
            </ul>

            <div className="flex items-center justify-between gap-4 bg-[#fff] px-4 py-2 rounded-md border-[#D2D2D2]">
              <p className="text-xs text-[#212121] font-medium">
                <b>Note</b>: Please upload the file in the same format as the
                downloaded template.
              </p>
              <button
                onClick={handleDownloadTemplate}
                className="bg-[#000000] text-white flex items-center space-x-1 p-2 rounded-md text-sm"
              >
                <img src="/img/csvxl.svg" alt="" />
                <span>Download</span>
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#D8D8D8] px-6 py-4 flex justify-end space-x-2 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-b-[30px]">
          <button
            onClick={handleUploadDoc}
            disabled={isBulkUploading || !file}
            className="bg-[#FBD037] hover:bg-yellow-400 text-[#000000] flex items-center rounded-full p-2 px-6 space-x-1 disabled:cursor-not-allowed text-sm font-medium"
          >
            {isBulkUploading ? "Uploading..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}
